import React, { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate } from 'react-router-dom';
import { isManagerMode } from '@utils/cookie';

// 客户端页面
const Home = lazy(() => import('@pages/client/Home').then(m => ({ default: m.Home })));
const CategoryList = lazy(() => import('@pages/client/CategoryList').then(m => ({ default: m.CategoryList })));
const ProductList = lazy(() => import('@pages/client/ProductList').then(m => ({ default: m.ProductList })));
const ProductDetail = lazy(() => import('@pages/client/ProductDetail').then(m => ({ default: m.ProductDetail })));
const Cart = lazy(() => import('@pages/client/Cart').then(m => ({ default: m.Cart })));
const Checkout = lazy(() => import('@pages/client/Checkout').then(m => ({ default: m.Checkout })));
const ClientOrders = lazy(() => import('@pages/client/Orders').then(m => ({ default: m.ClientOrders })));
const ClientOrderDetail = lazy(() => import('@pages/client/OrderDetail').then(m => ({ default: m.ClientOrderDetail })));
const Addresses = lazy(() => import('@pages/client/Addresses').then(m => ({ default: m.Addresses })));
const Login = lazy(() => import('@pages/client/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('@pages/client/Register').then(m => ({ default: m.Register })));
const Verify = lazy(() => import('@pages/client/Verify').then(m => ({ default: m.Verify })));
const NotFound = lazy(() => import('@pages/client/NotFound').then(m => ({ default: m.NotFound })));

// 管理后台页面
const AdminDashboard = lazy(() => import('@pages/admin/Dashboard').then(m => ({ default: m.AdminDashboard })));
const AdminLogin = lazy(() => import('@pages/admin/Login').then(m => ({ default: m.AdminLogin })));
const AdminRegister = lazy(() => import('@pages/admin/Register').then(m => ({ default: m.AdminRegister })));
const AdminProducts = lazy(() => import('@pages/admin/Products').then(m => ({ default: m.AdminProducts })));
const AdminImages = lazy(() => import('@pages/admin/Images').then(m => ({ default: m.AdminImages })));
const AdminProductDetail = lazy(() => import('@pages/admin/ProductDetail').then(m => ({ default: m.AdminProductDetail })));
const AdminCategories = lazy(() => import('@pages/admin/Categories').then(m => ({ default: m.AdminCategories })));
const AdminOrders = lazy(() => import('@pages/admin/Orders').then(m => ({ default: m.AdminOrders })));
const AdminOrderDetail = lazy(() => import('@pages/admin/OrderDetail').then(m => ({ default: m.AdminOrderDetail })));
const AdminComments = lazy(() => import('@pages/admin/Comments').then(m => ({ default: m.AdminComments })));
const AdminCoupons = lazy(() => import('@pages/admin/Coupons').then(m => ({ default: m.AdminCoupons })));
const AdminTraffic = lazy(() => import('@pages/admin/Traffic').then(m => ({ default: m.AdminTraffic })));
const AdminConsultations = lazy(() => import('@pages/admin/Consultations').then(m => ({ default: m.AdminConsultations })));
const AdminConsultationDetail = lazy(() => import('@pages/admin/ConsultationDetail').then(m => ({ default: m.AdminConsultationDetail })));
const AdminAnnouncement = lazy(() => import('@pages/admin/Announcement').then(m => ({ default: m.AdminAnnouncement })));

// 布局组件
import { AdminLayout } from '@components/layout/AdminLayout';

const Loading: React.FC = () => {
  const { t } = useTranslation('common');
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
    }}>
      {t('loading')}
    </div>
  );
};

// 受保护的路由组件：cookie 中 token 以 jpp_manager 为前缀则免登录，否则需登录
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!isManagerMode()) {
    return <Navigate to="/admin/login" replace />;
  }
  return <>{children}</>;
};

export const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* 客户端路由 */}
        <Route path="/" element={<CategoryList />} />
        <Route path="/company" element={<Home />} />
        <Route path="/categories" element={<CategoryList />} />
        <Route path="/products" element={<ProductList />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/orders" element={<ClientOrders />} />
        <Route path="/orders/:id" element={<ClientOrderDetail />} />
        <Route path="/addresses" element={<Addresses />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify" element={<Verify />} />
        
        {/* 管理后台路由 */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/register" element={<AdminRegister />} />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="images" element={<AdminImages />} />
          <Route path="products/:id" element={<AdminProductDetail />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="orders/:id" element={<AdminOrderDetail />} />
          <Route path="coupons" element={<AdminCoupons />} />
          <Route path="traffic" element={<AdminTraffic />} />
          <Route path="consultations" element={<AdminConsultations />} />
          <Route path="consultations/:uuid" element={<AdminConsultationDetail />} />
          <Route path="announcement" element={<AdminAnnouncement />} />
          <Route path="comments" element={<AdminComments />} />
        </Route>
        
        {/* 404 页面 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};
